import { z } from 'zod';

export const ConnectionIdConstArr = [
  'slack',
  'teams',
  'greenhouse',
  'lever',
  'fountain',
] as const;
export const ConnectionId = z.enum(ConnectionIdConstArr);
export type ConnectionId = z.infer<typeof ConnectionId>;

const EnabledConfig = <T extends z.ZodRawShape>(enabledShape: T) =>
  z.discriminatedUnion('isEnabled', [
    z.object({
      isEnabled: z.literal(false),
    }),
    z.object({
      isEnabled: z.literal(true),
      ...enabledShape,
    }),
  ]);

const SlackEnabledFields = {
  defaultChannelId: z.string().optional(),
};

const TeamsEnabledFields = {};

const GreenhouseEnabledFields = {};

const LeverEnabledFields = {};

const FountainEnabledFields = {
  /** OAuth services API root: …/api/serviceworkforce (workers) or …/api/servicehire/v2 (applicants). Required for OAuth2. */
  hireApiBaseUrl: z.string().url().optional(),
};

export const SlackConnectionConfigurationOptions =
  EnabledConfig(SlackEnabledFields);
export type SlackConnectionConfigurationOptions = z.infer<
  typeof SlackConnectionConfigurationOptions
>;

export const TeamsConnectionConfigurationOptions =
  EnabledConfig(TeamsEnabledFields);
export type TeamsConnectionConfigurationOptions = z.infer<
  typeof TeamsConnectionConfigurationOptions
>;

export const GreenhouseConnectionConfigurationOptions = EnabledConfig(
  GreenhouseEnabledFields,
);
export type GreenhouseConnectionConfigurationOptions = z.infer<
  typeof GreenhouseConnectionConfigurationOptions
>;

export const LeverConnectionConfigurationOptions =
  EnabledConfig(LeverEnabledFields);
export type LeverConnectionConfigurationOptions = z.infer<
  typeof LeverConnectionConfigurationOptions
>;

export const FountainConnectionConfigurationOptions = EnabledConfig(
  FountainEnabledFields,
);
export type FountainConnectionConfigurationOptions = z.infer<
  typeof FountainConnectionConfigurationOptions
>;

export const ConnectionConfigurationOptions = z.union([
  SlackConnectionConfigurationOptions,
  TeamsConnectionConfigurationOptions,
  GreenhouseConnectionConfigurationOptions,
  LeverConnectionConfigurationOptions,
  FountainConnectionConfigurationOptions,
]);
export type ConnectionConfigurationOptions = z.infer<
  typeof ConnectionConfigurationOptions
>;

export const ConnectionConfigurationRow = z.object({
  connectionId: ConnectionId,
  options: ConnectionConfigurationOptions,
});
export type ConnectionConfigurationRow = z.infer<
  typeof ConnectionConfigurationRow
>;

export const ConnectionsConfiguration = z.discriminatedUnion('isEnabled', [
  z.object({ isEnabled: z.literal(false) }),
  z.object({
    isEnabled: z.literal(true),
    connections: z.object({
      slack: SlackConnectionConfigurationOptions,
      teams: TeamsConnectionConfigurationOptions,
      greenhouse: GreenhouseConnectionConfigurationOptions,
      lever: LeverConnectionConfigurationOptions,
      fountain: FountainConnectionConfigurationOptions,
    }),
  }),
]);
export type ConnectionsConfiguration = z.infer<typeof ConnectionsConfiguration>;

export const ConnectionInstallation = z.discriminatedUnion('isInstalled', [
  z.object({ isInstalled: z.literal(false) }),
  z.object({ isInstalled: z.literal(true) }),
]);
export type ConnectionInstallation = z.infer<typeof ConnectionInstallation>;

const ResolvedEnabledConfig = <T extends z.ZodRawShape & { isEnabled?: never }>(
  enabledShape: T,
) =>
  z.discriminatedUnion('isEnabled', [
    z.object({ isEnabled: z.literal(false) }),
    z.object({
      isEnabled: z.literal(true),
      installation: ConnectionInstallation,
      ...enabledShape,
    }),
  ]);

export const ResolvedSlackConnection =
  ResolvedEnabledConfig(SlackEnabledFields);
export type ResolvedSlackConnection = z.infer<typeof ResolvedSlackConnection>;

export const ResolvedTeamsConnection =
  ResolvedEnabledConfig(TeamsEnabledFields);
export type ResolvedTeamsConnection = z.infer<typeof ResolvedTeamsConnection>;

export const ResolvedGreenhouseConnection = ResolvedEnabledConfig(
  GreenhouseEnabledFields,
);
export type ResolvedGreenhouseConnection = z.infer<
  typeof ResolvedGreenhouseConnection
>;

export const ResolvedLeverConnection =
  ResolvedEnabledConfig(LeverEnabledFields);

export type ResolvedLeverConnection = z.infer<typeof ResolvedLeverConnection>;

export const ResolvedFountainConnection = ResolvedEnabledConfig(
  FountainEnabledFields,
);

export type ResolvedFountainConnection = z.infer<
  typeof ResolvedFountainConnection
>;

export const ResolvedConnectionsConfiguration = z.discriminatedUnion(
  'isEnabled',
  [
    z.object({ isEnabled: z.literal(false) }),
    z.object({
      isEnabled: z.literal(true),
      connections: z.object({
        slack: ResolvedSlackConnection,
        teams: ResolvedTeamsConnection,
        greenhouse: ResolvedGreenhouseConnection,
        lever: ResolvedLeverConnection,
        fountain: ResolvedFountainConnection,
      }),
    }),
  ],
);
export type ResolvedConnectionsConfiguration = z.infer<
  typeof ResolvedConnectionsConfiguration
>;
